import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { tokens } from '../../global/theme/tokens';

// The notice the user acknowledges once (recorded server-side). When this
// text changes materially, bump EXO_AI_CHAT_NOTICE_VERSION in the backend so
// every user sees and acknowledges it again.
const NOTICE_POINTS = [
  'You are chatting with an AI assistant, not a person.',
  'Answers can be incomplete or wrong. Check them before you rely on them, especially figures, dates and anything you pass on.',
  'Your messages and the data the AI loads for them are sent to the AI provider configured for this ExoPaper instance.',
  'The AI acts on your data only within the permissions you grant it. You remain responsible for what it does on your behalf.',
];

/** Always visible line under the composer (AI Act Art. 50 transparency). */
export const AiChatNoticeLine = () => {
  const { t } = useTranslation();

  return (
    <Typography
      variant="caption"
      component="p"
      className="text-center px-4 pb-2 -mt-2"
      sx={{ opacity: 0.5, fontSize: '0.68rem', lineHeight: 1.3 }}
    >
      {t('ExoPaper AI can make mistakes. Check important information.')}
    </Typography>
  );
};

/**
 * Shown instead of the transcript and the composer until the user has
 * acknowledged the notice.
 */
export const AiChatAcknowledgementGate = ({ saving, onAcknowledge }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  return (
    <Box className="flex-1 min-h-0 overflow-y-auto flex flex-col justify-center px-4 pb-4">
      <Box
        className="flex flex-col gap-3 p-4 rounded-2xl mx-auto w-full"
        sx={{
          maxWidth: 440,
          backgroundColor: colors.card,
          border: `1px solid ${colors.info}40`,
        }}
      >
        <Box className="flex items-center gap-2">
          <Box
            className="flex items-center justify-center rounded-xl flex-shrink-0"
            sx={{
              width: 34,
              height: 34,
              backgroundColor: `${colors.info}20`,
              color: colors.info,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('Before you start')}
          </Typography>
        </Box>

        <Box component="ul" className="flex flex-col gap-2 pl-0 m-0">
          {NOTICE_POINTS.map((point) => (
            <Box key={point} component="li" className="flex gap-2 list-none">
              <InfoOutlinedIcon
                sx={{
                  fontSize: 16,
                  mt: '2px',
                  flexShrink: 0,
                  color: colors.info,
                }}
              />
              <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                {t(point)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box className="flex items-center gap-2 mt-1">
          <Typography variant="caption" sx={{ opacity: 0.5, flex: 1 }}>
            {t('We record that you have read this note.')}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            disabled={saving}
            onClick={onAcknowledge}
            sx={{ textTransform: 'none', borderRadius: '8px', px: 2 }}
          >
            {saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              t('I understand')
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
